import React from 'react';
import { Box, Divider, Paper, Typography, Stack, Chip } from '@mui/material';
import { Referral, ReferralStage, FlagSeverity } from '~/types/transition';
import ReferralCard from './ReferralCard';

const STAGE_LABELS: Record<ReferralStage, string> = {
  [ReferralStage.INTAKE]: 'Intake',
  [ReferralStage.PROCESSING]: 'Processing',
  [ReferralStage.DECISION]: 'Decision',
  [ReferralStage.EXECUTION]: 'Execution',
};

const STAGE_COLORS: Record<ReferralStage, string> = {
  [ReferralStage.INTAKE]: '#616161',
  [ReferralStage.PROCESSING]: '#1565c0',
  [ReferralStage.DECISION]: '#e65100',
  [ReferralStage.EXECUTION]: '#2e7d32',
};

const STAGE_BG: Record<ReferralStage, string> = {
  [ReferralStage.INTAKE]: '#f5f5f5',
  [ReferralStage.PROCESSING]: '#e3f2fd',
  [ReferralStage.DECISION]: '#fff8e1',
  [ReferralStage.EXECUTION]: '#e8f5e9',
};

interface PipelineColumnProps {
  stage: ReferralStage;
  referrals: Referral[];
}

const hasNonInfoFlag = (r: Referral) =>
  r.flags.some((f) => f.severity === FlagSeverity.WARNING || f.severity === FlagSeverity.BLOCKING);

const PipelineColumn: React.FC<PipelineColumnProps> = ({ stage, referrals }) => {
  const hasAnyBlocker = referrals.some((r) =>
    r.flags.some((f) => f.severity === FlagSeverity.BLOCKING)
  );

  const isDecision = stage === ReferralStage.DECISION;
  const needsReview = isDecision ? referrals.filter(hasNonInfoFlag) : [];
  const readyToDecide = isDecision ? referrals.filter((r) => !hasNonInfoFlag(r)) : [];
  const hasBothGroups = needsReview.length > 0 && readyToDecide.length > 0;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 240,
        maxWidth: 320,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Column header */}
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1,
          mb: 1,
          backgroundColor: STAGE_BG[stage],
          borderColor: hasAnyBlocker ? 'error.light' : 'divider',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ color: STAGE_COLORS[stage], letterSpacing: '0.08em', lineHeight: 1.5 }}
          >
            {STAGE_LABELS[stage]}
          </Typography>
          <Chip
            label={referrals.length}
            size="small"
            sx={{
              backgroundColor: STAGE_COLORS[stage],
              color: 'white',
              fontWeight: 700,
              height: 20,
              fontSize: '0.7rem',
              minWidth: 24,
            }}
          />
        </Stack>
        {stage === ReferralStage.PROCESSING && referrals.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {referrals.filter((r) => r.processingComplete).length} complete
            {' · '}
            {referrals.filter((r) => !r.processingComplete).length} in progress
          </Typography>
        )}
      </Paper>

      {/* Cards — scrollable */}
      <Box
        sx={{
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 'calc(100vh - 220px)',
          pr: 0.5,
        }}
      >
        {referrals.length === 0 ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ textAlign: 'center', pt: 3, display: 'block' }}
          >
            No referrals
          </Typography>
        ) : isDecision ? (
          <>
            {needsReview.map((referral) => (
              <ReferralCard key={referral.id} referral={referral} />
            ))}
            {hasBothGroups && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.6rem', letterSpacing: 0.6, whiteSpace: 'nowrap' }}
                >
                  READY TO DECIDE
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}
            {readyToDecide.map((referral) => (
              <ReferralCard key={referral.id} referral={referral} />
            ))}
          </>
        ) : (
          referrals.map((referral) => (
            <ReferralCard key={referral.id} referral={referral} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default PipelineColumn;
